import type { ContentCreator, ContentScope, ContentScopeFilters, ContentScopeOrigin } from '@/types/contentScope'

export const emptyContentScopeFilters = (): ContentScopeFilters => ({
  origin: 'all',
  creators: [],
  pongoTeam: false,
  search: '',
  statuses: []
})

export const normalizeContentScopeFilters = (filters?: Partial<ContentScopeFilters> | null): ContentScopeFilters => ({
  origin: filters?.origin ?? 'all',
  creators: [...(filters?.creators ?? [])],
  pongoTeam: filters?.pongoTeam ?? false,
  search: filters?.search ?? '',
  statuses: [...(filters?.statuses ?? [])]
})

const sameValues = <T extends string | number>(left: readonly T[], right: readonly T[]) =>
  [...left].sort().join() === [...right].sort().join()

export const sameContentScopeFilters = (a: Partial<ContentScopeFilters>, b: Partial<ContentScopeFilters>) => {
  const left = normalizeContentScopeFilters(a)
  const right = normalizeContentScopeFilters(b)

  return (
    left.origin === right.origin &&
    sameValues(left.creators, right.creators) &&
    left.pongoTeam === right.pongoTeam &&
    left.search === right.search &&
    sameValues(left.statuses, right.statuses)
  )
}

export const contentScopeParams = (filters: ContentScopeFilters) => ({
  ...(filters.origin !== 'all' && { scope: filters.origin }),
  ...(filters.creators.length > 0 && { created_by: filters.creators }),
  ...(filters.pongoTeam && { created_by_support: 1 })
})

export const contentScopeListParams = (filters: Partial<ContentScopeFilters>): Record<string, unknown> => {
  const normalized = normalizeContentScopeFilters(filters)

  return {
    ...contentScopeParams(normalized),
    ...(normalized.search && { search: normalized.search }),
    ...(normalized.statuses.length > 0 && { statuses: normalized.statuses })
  }
}

export const splitContentScopeListParams = (params: Record<string, unknown>) => {
  const { statuses, search, ...scopeParams } = params

  return {
    statuses: Array.isArray(statuses) ? statuses.map(String) : [],
    search: typeof search === 'string' ? search : '',
    scopeParams
  }
}

export const isContentScope = (value: unknown): value is ContentScope => value === 'network' || value === 'local'

export const isContentScopeOrigin = (value: unknown): value is ContentScopeOrigin =>
  value === 'all' || value === 'network' || value === 'local' || value === 'unclassified'

const isContentCreator = (value: unknown): value is ContentCreator =>
  !!value && typeof value === 'object' && 'id' in value && typeof value.id === 'number'

export const getRowContentScope = (data: unknown): ContentScope | null => {
  if (!data || typeof data !== 'object' || !('scope' in data)) return null
  return isContentScope(data.scope) ? data.scope : null
}

export const getRowContentCreator = (data: unknown): ContentCreator | null => {
  if (!data || typeof data !== 'object' || !('creator' in data)) return null
  return isContentCreator(data.creator) ? data.creator : null
}
